import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, PenLine, Loader2, CheckCircle2, FileCheck2,
} from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formularioService, formularioCampoService, formularioRespostaService } from '@/services/formulariosApi';
import { Formulario, FormularioCampo, FormularioArquivoEnviado, FormularioResposta } from '@/types';

function opcoesDe(campo: FormularioCampo): string[] {
  const cfg = (campo.configuracao || {}) as Record<string, unknown>;
  return Array.isArray(cfg.opcoes) ? (cfg.opcoes as string[]) : [];
}

function escalaDe(campo: FormularioCampo): { min: number; max: number } {
  const cfg = (campo.configuracao || {}) as Record<string, unknown>;
  return {
    min: typeof cfg.min === 'number' ? cfg.min : 1,
    max: typeof cfg.max === 'number' ? cfg.max : 5,
  };
}

function permiteOutroDe(campo: FormularioCampo): boolean {
  const cfg = (campo.configuracao || {}) as Record<string, unknown>;
  return cfg.permiteOutro === true;
}

const OUTRO_VALOR = '__outro__';

function erroDeData(tipo: 'data' | 'hora' | 'data_hora', valor: string): string | null {
  if (!valor) return null;
  if (tipo === 'hora') {
    const m = /^(\d{2}):(\d{2})$/.exec(valor);
    if (!m) return 'Horário em formato inválido.';
    const [, h, min] = m;
    if (Number(h) > 23 || Number(min) > 59) return 'Horário inexistente.';
    return null;
  }
  const dataParte = tipo === 'data' ? valor : valor.split('T')[0];
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dataParte);
  if (!m) return 'Data em formato inválido.';
  const [, ano, mes, dia] = m;
  const d = new Date(Number(ano), Number(mes) - 1, Number(dia));
  const valida = d.getFullYear() === Number(ano) && d.getMonth() === Number(mes) - 1 && d.getDate() === Number(dia);
  if (!valida) return 'Essa data não existe no calendário.';
  return null;
}

function CampoResposta({
  campo, valor, outroTexto, enviandoArquivo, onChange, onOutroTextoChange, onUpload,
}: {
  campo: FormularioCampo;
  valor: unknown;
  outroTexto: string;
  enviandoArquivo: boolean;
  onChange: (v: unknown) => void;
  onOutroTextoChange: (texto: string) => void;
  onUpload: (file: File) => void;
}) {
  const label = (
    <Label className="flex items-center gap-1">
      {campo.label}
      {campo.obrigatorio && <span className="text-destructive">*</span>}
    </Label>
  );

  switch (campo.tipo) {
    case 'texto_curto':
      return <div className="space-y-2">{label}<Input value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} /></div>;
    case 'texto_longo':
      return <div className="space-y-2">{label}<Textarea rows={3} className="text-left" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} /></div>;
    case 'numero':
      return <div className="space-y-2">{label}<Input type="number" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} /></div>;
    case 'data': {
      const erro = erroDeData('data', (valor as string) || '');
      return (
        <div className="space-y-2">
          {label}
          <Input type="date" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} />
          {erro && <p className="text-sm text-muted-foreground">{erro}</p>}
        </div>
      );
    }
    case 'hora': {
      const erro = erroDeData('hora', (valor as string) || '');
      return (
        <div className="space-y-2">
          {label}
          <Input type="time" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} />
          {erro && <p className="text-sm text-muted-foreground">{erro}</p>}
        </div>
      );
    }
    case 'data_hora': {
      const erro = erroDeData('data_hora', (valor as string) || '');
      return (
        <div className="space-y-2">
          {label}
          <Input type="datetime-local" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} />
          {erro && <p className="text-sm text-muted-foreground">{erro}</p>}
        </div>
      );
    }
    case 'lista':
      return (
        <div className="space-y-2">
          {label}
          <Select value={(valor as string) || undefined} onValueChange={onChange}>
            <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
            <SelectContent>
              {opcoesDe(campo).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    case 'selecao_unica': {
      const outroAtivo = permiteOutroDe(campo);
      return (
        <div className="space-y-2">
          {label}
          <RadioGroup value={(valor as string) || ''} onValueChange={onChange}>
            {opcoesDe(campo).map((o) => (
              <div key={o} className="flex items-center gap-2">
                <RadioGroupItem value={o} id={`${campo.id}-${o}`} />
                <Label htmlFor={`${campo.id}-${o}`} className="font-normal cursor-pointer">{o}</Label>
              </div>
            ))}
            {outroAtivo && (
              <div className="flex items-center gap-2">
                <RadioGroupItem value={OUTRO_VALOR} id={`${campo.id}-outro`} />
                <Label htmlFor={`${campo.id}-outro`} className="font-normal cursor-pointer">Outro</Label>
              </div>
            )}
          </RadioGroup>
          {outroAtivo && valor === OUTRO_VALOR && (
            <Input
              placeholder="Digite sua resposta..."
              value={outroTexto}
              onChange={(e) => onOutroTextoChange(e.target.value)}
              className="mt-1"
            />
          )}
        </div>
      );
    }
    case 'multipla_escolha': {
      const selecionadas = Array.isArray(valor) ? (valor as string[]) : [];
      const outroAtivo = permiteOutroDe(campo);
      return (
        <div className="space-y-2">
          {label}
          <div className="space-y-2">
            {opcoesDe(campo).map((o) => (
              <div key={o} className="flex items-center gap-2">
                <Checkbox
                  id={`${campo.id}-${o}`}
                  checked={selecionadas.includes(o)}
                  onCheckedChange={(checked) => {
                    onChange(checked ? [...selecionadas, o] : selecionadas.filter((s) => s !== o));
                  }}
                />
                <Label htmlFor={`${campo.id}-${o}`} className="font-normal cursor-pointer">{o}</Label>
              </div>
            ))}
            {outroAtivo && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${campo.id}-outro`}
                  checked={selecionadas.includes(OUTRO_VALOR)}
                  onCheckedChange={(checked) => {
                    onChange(checked ? [...selecionadas, OUTRO_VALOR] : selecionadas.filter((s) => s !== OUTRO_VALOR));
                  }}
                />
                <Label htmlFor={`${campo.id}-outro`} className="font-normal cursor-pointer">Outro</Label>
              </div>
            )}
          </div>
          {outroAtivo && selecionadas.includes(OUTRO_VALOR) && (
            <Input
              placeholder="Digite sua resposta..."
              value={outroTexto}
              onChange={(e) => onOutroTextoChange(e.target.value)}
              className="mt-1"
            />
          )}
        </div>
      );
    }
    case 'escala': {
      const { min, max } = escalaDe(campo);
      const opcoes = Array.from({ length: max - min + 1 }, (_, i) => min + i);
      return (
        <div className="space-y-2">
          {label}
          <div className="flex gap-2 flex-wrap">
            {opcoes.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => onChange(n)}
                className={`h-9 w-9 rounded-full border text-sm font-medium ${valor === n ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      );
    }
    case 'imagem':
    case 'arquivo': {
      const arquivo = valor as FormularioArquivoEnviado | undefined;
      return (
        <div className="space-y-2">
          {label}
          <div className="border border-dashed rounded-lg p-4 flex items-center gap-2 text-sm text-muted-foreground">
            {enviandoArquivo ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            ) : arquivo ? (
              <FileCheck2 className="h-4 w-4 shrink-0 text-emerald-600" />
            ) : (
              <Upload className="h-4 w-4 shrink-0" />
            )}
            <label className={`flex-1 truncate ${enviandoArquivo ? '' : 'cursor-pointer'}`}>
              {enviandoArquivo
                ? 'Enviando...'
                : arquivo
                  ? `${arquivo.nome} (trocar)`
                  : (campo.tipo === 'imagem' ? 'Enviar imagem ou tirar foto na hora' : 'Enviar arquivo ou tirar foto na hora')}
              <input
                type="file"
                className="hidden"
                disabled={enviandoArquivo}
                accept={campo.tipo === 'imagem' ? 'image/*' : undefined}
                capture="environment"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onUpload(file);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
        </div>
      );
    }
    case 'assinatura':
      return (
        <div className="space-y-2">
          {label}
          <div className="border border-dashed rounded-lg p-6 flex flex-col items-center justify-center gap-1 text-sm text-muted-foreground opacity-60">
            <PenLine className="h-5 w-5" />
            Assinatura eletrônica ainda não está disponível — em desenvolvimento.
          </div>
        </div>
      );
    default:
      return null;
  }
}

function valorVazio(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return true;
  if (Array.isArray(v) && v.length === 0) return true;
  return false;
}

export default function ResponderFormulario() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formulario, setFormulario] = useState<Formulario | null>(null);
  const [loading, setLoading] = useState(true);
  const [minhaResposta, setMinhaResposta] = useState<FormularioResposta | null>(null);
  const [fase, setFase] = useState<'resumo' | 'form'>('form');
  const [modo, setModo] = useState<'nova' | 'edicao'>('nova');
  const [valores, setValores] = useState<Record<string, unknown>>({});
  const [outroTextos, setOutroTextos] = useState<Record<string, string>>({});
  const [enviandoArquivoIds, setEnviandoArquivoIds] = useState<Set<string>>(new Set());
  const [enviando, setEnviando] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    const [resForm, resMinha] = await Promise.all([
      formularioService.getById(id),
      formularioRespostaService.minhaResposta(id),
    ]);
    if (resForm.success) setFormulario(resForm.data);
    else toast.error(resForm.error || 'Erro ao carregar formulário');

    const resposta = resMinha.success ? resMinha.data : null;
    setMinhaResposta(resposta);
    if (resposta && resForm.success && !resForm.data.permite_multiplas_respostas_por_pessoa) {
      setFase('resumo');
    } else {
      setFase('form');
      setModo('nova');
      setValores({});
      setOutroTextos({});
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const iniciarEdicao = () => {
    if (!minhaResposta) return;
    const v: Record<string, unknown> = {};
    for (const item of minhaResposta.valores || []) v[item.campo_id] = item.valor;
    setValores(v);
    setOutroTextos({});
    setModo('edicao');
    setFase('form');
  };

  const iniciarNovaResposta = () => {
    setValores({});
    setOutroTextos({});
    setModo('nova');
    setFase('form');
  };

  const handleUpload = async (campo: FormularioCampo, file: File) => {
    if (!formulario) return;
    setEnviandoArquivoIds((prev) => new Set(prev).add(campo.id));
    const res = await formularioCampoService.uploadArquivoResposta(formulario.id, campo.id, file);
    setEnviandoArquivoIds((prev) => {
      const next = new Set(prev);
      next.delete(campo.id);
      return next;
    });
    if (res.success) {
      setValores((prev) => ({ ...prev, [campo.id]: res.data }));
    } else {
      toast.error(res.error || 'Erro ao enviar arquivo');
    }
  };

  const construirPayload = (): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};
    for (const campo of formulario?.campos || []) {
      let v = valores[campo.id];
      if (v === undefined) continue;
      if (campo.tipo === 'selecao_unica' && v === OUTRO_VALOR) {
        v = { outro: true, texto: outroTextos[campo.id] || '' };
      }
      if (campo.tipo === 'multipla_escolha' && Array.isArray(v)) {
        v = (v as string[]).map((item) => (
          item === OUTRO_VALOR ? { outro: true, texto: outroTextos[campo.id] || '' } : item
        ));
      }
      payload[campo.id] = v;
    }
    return payload;
  };

  const handleEnviar = async () => {
    if (!formulario) return;
    const faltando = (formulario.campos || []).find((c) => c.obrigatorio && valorVazio(valores[c.id]));
    if (faltando) {
      toast.error(`O campo "${faltando.label}" é obrigatório`);
      return;
    }

    setEnviando(true);
    const payload = construirPayload();
    const res = modo === 'edicao' && minhaResposta
      ? await formularioRespostaService.atualizar(formulario.id, minhaResposta.id, payload)
      : await formularioRespostaService.submeter(formulario.id, payload);
    setEnviando(false);

    if (res.success) {
      toast.success(modo === 'edicao' ? 'Resposta atualizada' : 'Resposta enviada com sucesso');
      await load();
    } else {
      toast.error(res.error || 'Erro ao enviar resposta');
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-4 max-w-2xl mx-auto">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </AppLayout>
    );
  }

  if (!formulario) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto">Formulário não encontrado.</div>
      </AppLayout>
    );
  }

  if (formulario.status !== 'publicado') {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto space-y-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/formularios')}>
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Voltar
          </Button>
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              Este formulário ainda não está disponível para respostas.
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const campos = [...(formulario.campos || [])].sort((a, b) => a.ordem - b.ordem);

  return (
    <AppLayout>
      <div className="space-y-4 max-w-[84rem] mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate('/formularios')}>
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Voltar
        </Button>

        {fase === 'resumo' && minhaResposta ? (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="h-5 w-5" />
                <CardTitle className="text-lg">Você já respondeu este formulário</CardTitle>
              </div>
              <CardDescription>
                Enviado em {new Date(minhaResposta.respondido_em).toLocaleString('pt-BR')}.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex gap-2 flex-wrap">
              {formulario.permite_edicao_pelo_respondente && (
                <Button onClick={iniciarEdicao}>Editar minha resposta</Button>
              )}
              {formulario.permite_multiplas_respostas_por_pessoa && (
                <Button variant="outline" onClick={iniciarNovaResposta}>Responder novamente</Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>{formulario.titulo}</CardTitle>
              {formulario.descricao && <CardDescription>{formulario.descricao}</CardDescription>}
            </CardHeader>
            <CardContent className="space-y-5">
              {campos.length === 0 ? (
                <p className="text-sm text-muted-foreground">Este formulário ainda não tem campos.</p>
              ) : (
                campos.map((campo) => (
                  <CampoResposta
                    key={campo.id}
                    campo={campo}
                    valor={valores[campo.id]}
                    outroTexto={outroTextos[campo.id] || ''}
                    enviandoArquivo={enviandoArquivoIds.has(campo.id)}
                    onChange={(v) => setValores((prev) => ({ ...prev, [campo.id]: v }))}
                    onOutroTextoChange={(texto) => setOutroTextos((prev) => ({ ...prev, [campo.id]: texto }))}
                    onUpload={(file) => handleUpload(campo, file)}
                  />
                ))
              )}
              {campos.length > 0 && (
                <Button className="w-full" onClick={handleEnviar} disabled={enviando}>
                  {enviando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {modo === 'edicao' ? 'Salvar alterações' : 'Enviar'}
                </Button>
              )}
              {modo === 'edicao' && (
                <Button variant="ghost" className="w-full" onClick={() => setFase('resumo')} disabled={enviando}>
                  Cancelar
                </Button>
              )}
              {modo === 'nova' && minhaResposta && formulario.permite_multiplas_respostas_por_pessoa && (
                <p className="text-xs text-muted-foreground text-center">
                  Este formulário aceita mais de uma resposta sua —{' '}
                  <button type="button" className="underline" onClick={() => setFase('resumo')}>
                    ver minha última resposta
                  </button>.
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
